// Cell operations for @lexical/table, replacing the legacy 'tablesheet' cell menu.
//
// The delete/insert helpers in @lexical/table act on the current selection rather than on a given
// node, so every action first places the selection inside the target cell. Clicking the menu button
// can move focus out of the cell, which would otherwise make them act on the wrong cell (or throw).

import {
    $computeTableMapSkipCellCheck,
    $deleteTableColumnAtSelection,
    $deleteTableRowAtSelection,
    $getTableColumnIndexFromTableCellNode,
    $getTableNodeFromLexicalNodeOrThrow,
    $getTableRowIndexFromTableCellNode,
    $insertTableColumnAtSelection,
    $insertTableRowAtSelection,
    $isTableCellNode,
    $unmergeCell,
    TableCellHeaderStates,
    TableCellNode,
} from "@lexical/table"
import {$createParagraphNode, $getNodeByKey, LexicalEditor, NodeKey} from "lexical"

const debug = false
// const debug = "TableActions"

// Runs fn with the selection anchored inside the given cell
const withCell = (editor: LexicalEditor, cellKey: NodeKey, fn: (cell: TableCellNode) => void) => {
    editor.update(() => {
        const cell = $getNodeByKey(cellKey)
        if (!$isTableCellNode(cell)) {
            debug && console.log(debug, 'cell not found', cellKey)
            return
        }

        cell.selectStart()
        fn(cell)
    })
}

export const tableInsertRowAbove = (editor: LexicalEditor, cellKey: NodeKey) => {
    return withCell(editor, cellKey, () => {
        return $insertTableRowAtSelection(false)
    })
}

export const tableInsertRowBelow = (editor: LexicalEditor, cellKey: NodeKey) => {
    return withCell(editor, cellKey, () => {
        return $insertTableRowAtSelection(true)
    })
}

export const tableInsertColumnLeft = (editor: LexicalEditor, cellKey: NodeKey) => {
    return withCell(editor, cellKey, () => {
        return $insertTableColumnAtSelection(false)
    })
}

export const tableInsertColumnRight = (editor: LexicalEditor, cellKey: NodeKey) => {
    return withCell(editor, cellKey, () => {
        return $insertTableColumnAtSelection(true)
    })
}

export const tableDeleteRow = (editor: LexicalEditor, cellKey: NodeKey) => {
    return withCell(editor, cellKey, () => {
        return $deleteTableRowAtSelection()
    })
}

export const tableDeleteColumn = (editor: LexicalEditor, cellKey: NodeKey) => {
    return withCell(editor, cellKey, () => {
        return $deleteTableColumnAtSelection()
    })
}

export const tableDeleteTable = (editor: LexicalEditor, cellKey: NodeKey) => {
    return withCell(editor, cellKey, cell => {
        const table = $getTableNodeFromLexicalNodeOrThrow(cell)
        table.selectNext()
        return table.remove()
    })
}

export const tableCellIsRowHeader = (cell: TableCellNode) => {
    return cell.hasHeaderState(TableCellHeaderStates.ROW)
}

export const tableCellIsColumnHeader = (cell: TableCellNode) => {
    return cell.hasHeaderState(TableCellHeaderStates.COLUMN)
}

// Sets/clears a single header bit on every cell, leaving their other header bit untouched. Cells
// spanning multiple rows/columns appear more than once in a table map row/column - dedupe by key
// so a merged cell isn't toggled back and forth.
const setHeaderBitOnCells = (cells: TableCellNode[], bit: number, addHeader: boolean) => {
    const seenKeys = new Set<NodeKey>()

    for (const cell of cells) {
        if (seenKeys.has(cell.getKey())) {
            continue
        }

        seenKeys.add(cell.getKey())
        cell.setHeaderStyles(addHeader ? bit : TableCellHeaderStates.NO_STATUS, bit)
    }
}

export const tableToggleRowHeader = (editor: LexicalEditor, cellKey: NodeKey) => {
    return withCell(editor, cellKey, cell => {
        const tableNode  = $getTableNodeFromLexicalNodeOrThrow(cell)
        const rowIndex   = $getTableRowIndexFromTableCellNode(cell)
        const [tableMap] = $computeTableMapSkipCellCheck(tableNode, null, null)
        const addHeader  = !tableCellIsRowHeader(cell)

        setHeaderBitOnCells(tableMap[rowIndex].map(value => value.cell), TableCellHeaderStates.ROW, addHeader)
    })
}

export const tableToggleColumnHeader = (editor: LexicalEditor, cellKey: NodeKey) => {
    return withCell(editor, cellKey, cell => {
        const tableNode   = $getTableNodeFromLexicalNodeOrThrow(cell)
        const columnIndex = $getTableColumnIndexFromTableCellNode(cell)
        const [tableMap]  = $computeTableMapSkipCellCheck(tableNode, null, null)
        const addHeader   = !tableCellIsColumnHeader(cell)

        setHeaderBitOnCells(tableMap.map(row => row[columnIndex].cell), TableCellHeaderStates.COLUMN, addHeader)
    })
}

export const tableUnmergeCell = (editor: LexicalEditor, cellKey: NodeKey) => {
    return withCell(editor, cellKey, () => {
        return $unmergeCell()
    })
}

// TableCellNode.canBeEmpty() is false - always leave a paragraph behind
export const tableClearCell = (editor: LexicalEditor, cellKey: NodeKey) => {
    return withCell(editor, cellKey, cell => {
        cell.clear()
        return cell.append($createParagraphNode())
    })
}

export const tableCellIsMerged = (cell: TableCellNode) => {
    return cell.getColSpan() > 1 || cell.getRowSpan() > 1
}
